import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FaceIcon from '@mui/icons-material/Face';
import GroupsIcon from '@mui/icons-material/Groups';
import TimelineIcon from '@mui/icons-material/Timeline';
import SecurityIcon from '@mui/icons-material/Security';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import CelebrationIcon from '@mui/icons-material/Celebration';
import LandingHeader from '../../components/common/LandingHeader';
import Footer from '../../components/common/Footer';
import ContactUsDialog from './ContactUsDialog';
import ActiveAutoSyncPill from '../../components/common/ActiveAutoSyncPill';

// ── Sub-components ────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index, ...other }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
    sx={{
      display: value !== index ? 'none' : 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
      gap: 3,
    }}
  >
    {value === index && children}
  </Box>
);

const FeatureBullet = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
    <CheckCircleIcon sx={{ color: 'primary.main', mt: 0.3, flexShrink: 0, fontSize: 20 }} />
    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
      {text}
    </Typography>
  </Box>
);

const StatItem = ({ number, label, icon }) => (
  <Box sx={{ textAlign: 'center', px: { xs: 1, md: 3 } }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{icon}</Box>
    <Typography variant="h5" fontWeight={800} color="primary.main" sx={{ mb: 0.5 }}>
      {number}
    </Typography>
    <Typography variant="body2" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
  </Box>
);

const StepCard = ({ step, title, body }) => (
  <Box
    sx={{
      p: 4,
      bgcolor: 'background.paper',
      borderRadius: 3,
      boxShadow: 1,
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.3s',
      '&:hover': { boxShadow: 3 },
      '&::before': {
        content: `"${step}"`,
        position: 'absolute',
        top: -14,
        right: 14,
        fontSize: '7rem',
        fontWeight: 900,
        color: 'primary.main',
        opacity: 0.05,
        lineHeight: 1,
        pointerEvents: 'none',
      },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        mb: 2,
        fontSize: '1rem',
      }}
    >
      {step}
    </Box>
    <Typography variant="h6" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
      {body}
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const Landing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [activeDemo, setActiveDemo] = useState(0);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const DEMO_COUNT = 5;

  useEffect(() => {
    const t = setInterval(() => setActiveDemo((d) => (d + 1) % DEMO_COUNT), 4000);
    return () => clearInterval(t);
  }, []);

  const handleAuth = () => {
    window.scrollTo(0, 0);
    navigate('/auth');
  };

  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);
  const particlesLoaded = useCallback(async () => {}, []);

  const painPoints = [
    {
      icon: <PhotoLibraryIcon sx={{ fontSize: 42, color: '#ef5350' }} />,
      title: 'Photos scattered everywhere',
      description:
        "Event photos end up across WhatsApp groups, email threads, and shared drives. Finding one specific photo from last year's reunion feels impossible.",
    },
    {
      icon: <SearchIcon sx={{ fontSize: 42, color: '#ef5350' }} />,
      title: "Can't find the moment you remember",
      description:
        'You remember it perfectly - laughing at the dinner table, group shot on the terrace - but scrolling through thousands of photos takes forever.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 42, color: '#ef5350' }} />,
      title: 'Sharing is always a mess',
      description:
        "Your family doesn't need to see your work event photos. Your colleagues shouldn't see the family reunion. But separating them manually is exhausting.",
    },
  ];

  const organizerFeatures = [
    { icon: <QrCodeIcon />, title: 'Instant QR Join', description: 'Generate a unique QR code. Attendees scan and join in seconds - no app download needed.' },
    { icon: <AutoAwesomeIcon />, title: 'AI Face Grouping', description: 'Automatically cluster photos by person across the entire event. Tag once, find everywhere.' },
    { icon: <GroupsIcon />, title: 'Circle-Based Access', description: 'VIPs, family, colleagues, vendors - each circle sees only what you intend for them.' },
    { icon: <CloudUploadIcon />, title: 'Bulk Upload & Drive Sync', description: 'Upload thousands of photos at once, or pull directly from Google Drive with one click.' },
    { icon: <PeopleAltIcon />, title: 'Crowdsource Every Shot', description: 'Request photos from every attendee. Never miss a candid moment from any camera in the room.' },
    { icon: <TimelineIcon />, title: 'Event Timelines', description: 'Beautiful, shareable timelines for conferences, weddings, reunions, and more.' },
  ];

  const attendeeSteps = [
    { step: '1', title: 'Create Your Profile', body: 'Sign up and add a profile photo. Your photo helps the AI find you automatically in event pictures.' },
    { step: '2', title: 'Scan & Join', body: 'Scan the event QR code to join instantly. Connect with the organiser and set your relationship for the social tree.' },
    { step: '3', title: 'Your Photos, Delivered', body: 'After the event, receive every photo featuring you - automatically identified by AI, no manual searching.' },
    { step: '4', title: 'Search Your Memories', body: 'Use natural language to find any moment across all your events. Build a lifetime of searchable memories.' },
  ];

  const organizerSteps = [
    { step: '1', title: 'Create Your Event', body: 'Set up your event in minutes. Generate a unique QR code and invite link attendees can use instantly.' },
    { step: '2', title: 'Invite & Manage Guests', body: 'Send invites through the app or share the QR code. Use circles to automatically curate who sees what.' },
    { step: '3', title: 'Collect All the Photos', body: 'Upload from any device or Google Drive. Request photos from attendees - crowdsource every angle.' },
    { step: '4', title: 'AI Organises Everything', body: 'Face grouping identifies every person. Circles distribute photos automatically. You just enjoy the results.' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ActiveAutoSyncPill />
      <LandingHeader />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <Box
        id="home"
        sx={{
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #060d1f 0%, #0d1b3e 40%, #13235c 75%, #1a1060 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Glowing orbs */}
        <Box sx={{
          position: 'absolute', width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33,150,243,0.18) 0%, transparent 70%)',
          top: '-20%', left: '-10%', pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(92,107,192,0.2) 0%, transparent 70%)',
          bottom: '-15%', right: '-5%', pointerEvents: 'none',
        }} />

        <Box sx={{ position: 'absolute', inset: 0, height: '100%' }}>
          <Particles
            id="tsparticles-home"
            init={particlesInit}
            loaded={particlesLoaded}
            options={{
              fullScreen: { enable: false },
              background: { opacity: 0 },
              fpsLimit: 60,
              interactivity: {
                events: {
                  onHover: { enable: true, mode: 'repulse' },
                  resize: true,
                },
                modes: { repulse: { distance: 80, duration: 0.4 } },
              },
              particles: {
                color: { value: ['#90caf9', '#ce93d8', '#ffffff'] },
                links: { color: '#90caf9', distance: 140, enable: true, opacity: 0.12, width: 1 },
                move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, speed: 0.6 },
                number: { density: { enable: true, area: 1000 }, value: 50 },
                opacity: { value: { min: 0.05, max: 0.25 } },
                shape: { type: 'circle' },
                size: { value: { min: 1, max: 3 } },
              },
              detectRetina: true,
            }}
            style={{ position: 'absolute', inset: 0, height: '100%' }}
          />
        </Box>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: { xs: 8, md: 10 },
            }}
          >
            {/* Left: Copy */}
            <Box sx={{ width: '100%', textAlign: { xs: 'center', md: 'left' } }}>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
                <Chip
                  label="✨  Your Events Understood"
                  sx={{
                    mb: 3, fontWeight: 700, fontSize: '0.85rem', borderRadius: 6,
                    bgcolor: 'rgba(144,202,249,0.12)', color: '#90caf9',
                    border: '1px solid rgba(144,202,249,0.3)',
                  }}
                />
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.08 }}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: { xs: 'center', md: 'flex-start' }, mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src="/circle-logo-2.png"
                    alt="Circle Moments Logo"
                    sx={{ height: { xs: 90, md: 130 }, width: 'auto', filter: 'brightness(0) invert(1)' }}
                  />
                </Box>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.3rem', sm: '3rem', md: '3.8rem' },
                    fontWeight: 900,
                    color: 'white',
                    lineHeight: 1.1,
                    mb: 2.5,
                    letterSpacing: '-0.5px',
                  }}
                >
                  Your Events  {' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(90deg, #60a5fa 0%, #f472b6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Understood.
                  </Box>
                </Typography>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.18 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                    lineHeight: 1.8,
                    mb: 4,
                    fontWeight: 400,
                    maxWidth: 540,
                    mx: { xs: 'auto', md: 0 },
                    color: 'rgba(255,255,255,0.72)',
                  }}
                >
                  Share photos through a selfie - then let AI understand who was present,
                  what happened, and how everyone connects. Search by memory. Share by relationship.
                </Typography>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.28 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleAuth}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      py: 1.75, px: 4, fontSize: '1rem', fontWeight: 700, borderRadius: 2,
                      textTransform: 'none',
                      bgcolor: '#2196F3',
                      boxShadow: '0 0 32px rgba(33,150,243,0.45)',
                      '&:hover': { bgcolor: '#1976D2', boxShadow: '0 0 42px rgba(33,150,243,0.6)' },
                    }}
                  >
                    Get Started Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component="a"
                    href="#how-it-works"
                    sx={{
                      py: 1.75, px: 3.5, fontSize: '1rem', fontWeight: 600, borderRadius: 2,
                      textTransform: 'none', borderColor: 'rgba(255,255,255,0.3)', color: 'white',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    See How It Works
                  </Button>
                </Box>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}>
                <Box
                  sx={{
                    mt: 4, display: 'flex', gap: 3, flexWrap: 'wrap',
                    justifyContent: { xs: 'center', md: 'flex-start' }, alignItems: 'center',
                  }}
                >
                  {['Finds photos by person, moment, or event', 'Remembers faces across every event', 'Share the right photos automatically'].map((t) => (
                    <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CheckCircleIcon sx={{ color: '#80cbc4', fontSize: 17 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Box>

            {/* Right: Animated feature demo carousel */}
            <Box
              sx={{
                width: '100%',
                flexShrink: 0,
                maxWidth: { xs: 360, md: 460 },
                mx: 'auto',
              }}
            >
              {/* Glassy card wrapper */}
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  p: 3,
                  boxShadow: '0 16px 64px rgba(0,0,0,0.5)',
                  minHeight: { xs: 290, md: 330 },
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <AnimatePresence mode="wait">
                  {activeDemo === 0 && (
                    <motion.div
                      key="selfie"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.38 }}
                    >
                      {/* Selfie Share demo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <PhotoCameraIcon sx={{ color: '#f48fb1', fontSize: 20 }} />
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                          Share via Selfie
                        </Typography>
                        <Chip
                          label="Instant Match"
                          size="small"
                          sx={{ ml: 'auto', bgcolor: 'rgba(244,143,177,0.15)', color: '#f48fb1', fontSize: '0.7rem', height: 22 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'rgba(244,143,177,0.08)', borderRadius: 2, py: 3, mb: 2,
                          border: '1px dashed rgba(244,143,177,0.35)',
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar
                            sx={{
                              width: 56, height: 56, bgcolor: '#f48fb1', mx: 'auto', mb: 1,
                              border: '2px solid rgba(255,255,255,0.15)',
                            }}
                          >
                            <FaceIcon sx={{ fontSize: 30, color: 'white' }} />
                          </Avatar>
                          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem' }}>
                            Take a selfie to find yourself
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, mb: 1.5 }}>
                        {['#f48fb1', '#9b59b6', '#27ae60', '#e67e22', '#4a90d9', '#e74c3c'].map((bg, i) => (
                          <Box
                            key={i}
                            sx={{
                              aspectRatio: '1/1', borderRadius: 1.5,
                              background: `linear-gradient(135deg, ${bg}55, ${bg}22)`,
                              border: `1px solid ${bg}35`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <PhotoLibraryIcon sx={{ color: bg, fontSize: 18, opacity: 0.75 }} />
                          </Box>
                        ))}
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.73rem', textAlign: 'right' }}>
                        18 of your photos found instantly
                      </Typography>
                    </motion.div>
                  )}

                  {activeDemo === 1 && (
                    <motion.div
                      key="search"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.38 }}
                    >
                      {/* AI Search demo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <SearchIcon sx={{ color: '#90caf9', fontSize: 20 }} />
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                          AI Moments Search
                        </Typography>
                        <Chip
                          label="AI Planner"
                          size="small"
                          sx={{ ml: 'auto', bgcolor: 'rgba(144,202,249,0.15)', color: '#90caf9', fontSize: '0.7rem', height: 22 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 2, px: 2, py: 1.25,
                          display: 'flex', alignItems: 'center', gap: 1, mb: 2,
                          border: '1px solid rgba(144,202,249,0.25)',
                        }}
                      >
                        <SearchIcon sx={{ color: '#90caf9', fontSize: 17 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', flex: 1 }}>
                          dancing at the wedding reception…
                        </Typography>
                        <Box
                          sx={{
                            width: 26, height: 26, borderRadius: '50%', bgcolor: '#2196F3', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 0 10px rgba(33,150,243,0.5)',
                          }}
                        >
                          <AutoAwesomeIcon sx={{ color: 'white', fontSize: 14 }} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, mb: 1.5 }}>
                        {['#4a90d9', '#9b59b6', '#27ae60', '#e67e22', '#e74c3c', '#1abc9c'].map((bg, i) => (
                          <Box
                            key={i}
                            sx={{
                              aspectRatio: '1/1', borderRadius: 1.5,
                              background: `linear-gradient(135deg, ${bg}55, ${bg}22)`,
                              border: `1px solid ${bg}35`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <PhotoLibraryIcon sx={{ color: bg, fontSize: 18, opacity: 0.75 }} />
                          </Box>
                        ))}
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.73rem', textAlign: 'right' }}>
                        24 photos found across 3 events
                      </Typography>
                    </motion.div>
                  )}

                  {activeDemo === 2 && (
                    <motion.div
                      key="face"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.38 }}
                    >
                      {/* Face Memory demo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <FaceIcon sx={{ color: '#ce93d8', fontSize: 20 }} />
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                          Smart Face Memory
                        </Typography>
                        <Chip
                          label="6 named"
                          size="small"
                          sx={{ ml: 'auto', bgcolor: 'rgba(206,147,216,0.15)', color: '#ce93d8', fontSize: '0.7rem', height: 22 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        {[
                          { initial: 'S', name: 'Skanda', color: '#e91e63' },
                          { initial: 'R', name: 'Rahul', color: '#2196f3' },
                          { initial: 'J', name: 'John', color: '#4caf50' },
                          { initial: 'K', name: 'Kumar', color: '#ff9800' },
                          { initial: 'P', name: 'Priya', color: '#9c27b0' },
                          { initial: 'J', name: 'James', color: '#00bcd4' },
                        ].map(({ initial, name, color }) => (
                          <Box key={initial} sx={{ textAlign: 'center' }}>
                            <Avatar
                              sx={{
                                width: 44, height: 44, bgcolor: color, mx: 'auto', mb: 0.5,
                                fontSize: '1rem', fontWeight: 800,
                                border: '2px solid rgba(255,255,255,0.12)',
                              }}
                            >
                              {initial}
                            </Avatar>
                            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem' }}>
                              {name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          bgcolor: 'rgba(206,147,216,0.1)', borderRadius: 2, px: 1.5, py: 1,
                          border: '1px solid rgba(206,147,216,0.2)',
                        }}
                      >
                        <AutoAwesomeIcon sx={{ color: '#ce93d8', fontSize: 15 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem' }}>
                          847 photos tagged · across 5 events
                        </Typography>
                      </Box>
                    </motion.div>
                  )}

                  {activeDemo === 3 && (
                    <motion.div
                      key="requests"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.38 }}
                    >
                      {/* Photo Requests demo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <PeopleAltIcon sx={{ color: '#80cbc4', fontSize: 20 }} />
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                          Photo Requests
                        </Typography>
                        <Chip
                          label="3 active"
                          size="small"
                          sx={{ ml: 'auto', bgcolor: 'rgba(128,203,196,0.15)', color: '#80cbc4', fontSize: '0.7rem', height: 22 }}
                        />
                      </Box>

                      {/* Collection progress bar */}
                      <Box sx={{ mb: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem' }}>
                            Photos collected from guests
                          </Typography>
                          <Typography sx={{ color: '#80cbc4', fontSize: '0.75rem', fontWeight: 700 }}>
                            73%
                          </Typography>
                        </Box>
                        <Box sx={{ height: 6, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                          <Box sx={{ width: '73%', height: '100%', bgcolor: '#80cbc4', borderRadius: 3 }} />
                        </Box>
                      </Box>

                      {[
                        { name: 'Wedding Reception Photos', count: '31 photos', done: true },
                        { name: 'Pre-wedding Ceremony', count: '24 photos', done: true },
                        { name: 'Mehendi Evening', count: '8 guests pending', done: false },
                      ].map((r, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, py: 1,
                            borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                          }}
                        >
                          <Box
                            sx={{
                              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                              bgcolor: r.done ? 'rgba(128,203,196,0.2)' : 'rgba(255,193,7,0.15)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <CheckCircleIcon sx={{ color: r.done ? '#80cbc4' : '#ffc107', fontSize: 14 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', fontWeight: 600 }}>
                              {r.name}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.7rem' }}>
                              {r.count}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </motion.div>
                  )}

                  {activeDemo === 4 && (
                    <motion.div
                      key="circles"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.38 }}
                    >
                      {/* Circle Sharing demo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <GroupsIcon sx={{ color: '#ffcc80', fontSize: 20 }} />
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                          Shared to Circles
                        </Typography>
                        <Chip
                          label="Sent ✓"
                          size="small"
                          sx={{ ml: 'auto', bgcolor: 'rgba(255,204,128,0.15)', color: '#ffcc80', fontSize: '0.7rem', height: 22 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
                        {[
                          { label: 'Family', icon: <FamilyRestroomIcon sx={{ fontSize: 18 }} />, color: '#e91e63', people: 12, photos: 240 },
                          { label: 'Work Team', icon: <BusinessIcon sx={{ fontSize: 18 }} />, color: '#2196f3', people: 28, photos: 95 },
                          { label: 'Close Friends', icon: <GroupsIcon sx={{ fontSize: 18 }} />, color: '#4caf50', people: 15, photos: 182 },
                          { label: 'VIP Guests', icon: <EmojiEventsIcon sx={{ fontSize: 18 }} />, color: '#ff9800', people: 8, photos: 64 },
                        ].map((c) => (
                          <Box
                            key={c.label}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1.5,
                              bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, px: 1.5, py: 1,
                              border: `1px solid ${c.color}22`,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                bgcolor: `${c.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: c.color,
                              }}
                            >
                              {c.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.82rem' }}>{c.label}</Typography>
                              <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.7rem' }}>
                                {c.people} people · {c.photos} photos
                              </Typography>
                            </Box>
                            <CheckCircleIcon sx={{ color: c.color, fontSize: 16, opacity: 0.8 }} />
                          </Box>
                        ))}
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.73rem', textAlign: 'center' }}>
                        63 people received their personalised view
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>

              {/* Navigation dots */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2.5, alignItems: 'center' }}>
                {[
                  { color: '#f48fb1', label: 'Selfie' },
                  { color: '#90caf9', label: 'Search' },
                  { color: '#ce93d8', label: 'Faces' },
                  { color: '#80cbc4', label: 'Requests' },
                  { color: '#ffcc80', label: 'Circles' },
                ].map(({ color, label }, i) => (
                  <Box
                    key={i}
                    onClick={() => setActiveDemo(i)}
                    title={label}
                    sx={{
                      width: i === activeDemo ? 28 : 8, height: 8, borderRadius: 4,
                      bgcolor: i === activeDemo ? color : 'rgba(255,255,255,0.2)',
                      cursor: 'pointer', transition: 'all 0.35s ease',
                      '&:hover': { bgcolor: color, opacity: 0.8 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── STATS BAR ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          py: 5,
          bgcolor: 'white',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 4 }}>
            <StatItem
              number="Natural Language"
              label="Photo Search"
              icon={<SearchIcon sx={{ color: 'primary.light', fontSize: 34 }} />}
            />
            <StatItem
              number="AI Face"
              label="Cross-Event Recognition"
              icon={<FaceIcon sx={{ color: 'primary.light', fontSize: 34 }} />}
            />
            <StatItem
              number="Smart Circles"
              label="Privacy-First Sharing"
              icon={<ConnectWithoutContactIcon sx={{ color: 'primary.light', fontSize: 34 }} />}
            />
            <StatItem
              number="100%"
              label="You Control Your Data"
              icon={<SecurityIcon sx={{ color: 'primary.light', fontSize: 34 }} />}
            />
          </Box>
        </Container>
      </Box>

      {/* ── PAIN POINTS ──────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="overline"
              color="error.main"
              fontWeight={700}
              display="block"
              align="center"
              sx={{ mb: 1, letterSpacing: 3 }}
            >
              THE PROBLEM
            </Typography>
            <Typography
              variant="h3"
              align="center"
              fontWeight={800}
              sx={{ mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              Sound familiar?
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 580, mx: 'auto', mb: 8, fontSize: '1.08rem', lineHeight: 1.7 }}
            >
              Every event ends the same way - hundreds of photos, zero organisation,
              and memories slowly fading into forgotten folders.
            </Typography>
          </motion.div>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 4,
            }}
          >
            {painPoints.map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
              >
                <Box
                  sx={{
                    p: 4,
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: 1,
                    height: '100%',
                    borderTop: '4px solid #ef5350',
                  }}
                >
                  <Box sx={{ mb: 2 }}>{point.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {point.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                    {point.description}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Box sx={{ textAlign: 'center', mt: 7 }}>
              <Typography variant="h5" fontWeight={800} color="primary.main">
                CircleMoments solves all of this. ✓
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ── FEATURE 1: AI MOMENTS SEARCH ─────────────────────────────────────── */}
      <Box id="features" sx={{ py: { xs: 8, md: 14 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 6, md: 10 },
              alignItems: 'center',
            }}
          >
            {/* Image slot */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ flex: 1, width: '100%' }}
            >
              {/* Replace the Box below with <img src="..." /> once screenshot is ready */}
              <Box
                sx={{
                  width: '100%',
                  minHeight: 340,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #90caf9',
                  p: 4,
                  gap: 2,
                }}
              >
                <SearchIcon sx={{ fontSize: 60, color: '#90caf9' }} />
                <Typography color="text.disabled" align="center" variant="body2">
                  [Screenshot: Moments search bar with query and photo grid results]
                </Typography>
              </Box>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ flex: 1 }}
            >
              <Box>
                <Chip label="Moments Search" color="primary" sx={{ mb: 2.5, fontWeight: 700 }} />
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{ mb: 2, fontSize: { xs: '1.85rem', md: '2.3rem' }, lineHeight: 1.2 }}
                >
                  Find any photo by describing the moment
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.05rem' }}
                >
                  Forget scrolling through thousands of photos. Just describe what you remember -
                  our AI understands context, people, emotion, and setting. No tagging, no folders,
                  no frustration.
                </Typography>
                <FeatureBullet text='"Find photos of us dancing at the wedding reception"' />
                <FeatureBullet text='"Group shots from the conference dinner last year"' />
                <FeatureBullet text='"Photos with my grandmother from any event"' />
                <FeatureBullet text="Powered by visual AI + language models - searches all your events at once" />
                <FeatureBullet text="Add personal context notes to make search smarter for your world" />
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* ── FEATURE 2: SMART FACE MEMORY ─────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 14 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row-reverse' },
              gap: { xs: 6, md: 10 },
              alignItems: 'center',
            }}
          >
            {/* Image slot */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ flex: 1, width: '100%' }}
            >
              {/* Replace the Box below with <img src="..." /> once screenshot is ready */}
              <Box
                sx={{
                  width: '100%',
                  minHeight: 340,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f3e5f5 0%, #ede7f6 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ce93d8',
                  p: 4,
                  gap: 2,
                }}
              >
                <FaceIcon sx={{ fontSize: 60, color: '#ce93d8' }} />
                <Typography color="text.disabled" align="center" variant="body2">
                  [Screenshot: Face memory database with named clusters and notes]
                </Typography>
              </Box>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ flex: 1 }}
            >
              <Box>
                <Chip label="Smart Face Memory" color="secondary" sx={{ mb: 2.5, fontWeight: 700 }} />
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{ mb: 2, fontSize: { xs: '1.85rem', md: '2.3rem' }, lineHeight: 1.2 }}
                >
                  Every face, every event - forever remembered
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.05rem' }}
                >
                  Our AI automatically recognises and groups people across all your events. Name
                  them once - CircleMoments remembers them across years of memories, building
                  a rich personal photo database you actually own.
                </Typography>
                <FeatureBullet text="Auto-detect and cluster faces from hundreds of photos instantly" />
                <FeatureBullet text="Build a persistent memory of every person you've photographed" />
                <FeatureBullet text="Find all photos of a specific person across any event, any year" />
                <FeatureBullet text="Add names, nicknames, relationships, and personal notes" />
                <FeatureBullet text="Link faces to your personal network - bridging memory and connection" />
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* ── FEATURE 3: CIRCLES + PRIVACY ─────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 14 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 6, md: 10 },
              alignItems: 'center',
            }}
          >
            {/* Visual: Circle diagram */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ flex: 1 }}
            >
              <Box
                sx={{
                  width: '100%',
                  minHeight: 340,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ position: 'relative', width: 260, height: 260 }}>
                  {/* Centre */}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)',
                      border: '2px solid #90caf9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <PhotoLibraryIcon sx={{ fontSize: 52, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight={700} color="primary.main" align="center">
                      Your Event
                    </Typography>
                  </Box>

                  {/* Orbiting circles */}
                  {[
                    { label: 'Family', icon: <FamilyRestroomIcon />, color: '#e91e63', top: -36, left: '50%', ml: '-40px' },
                    { label: 'Work', icon: <BusinessIcon />, color: '#2196f3', top: '38%', right: -52, mt: '-40px' },
                    { label: 'Friends', icon: <GroupsIcon />, color: '#4caf50', bottom: -36, left: '50%', ml: '-40px' },
                    { label: 'VIP', icon: <EmojiEventsIcon />, color: '#ff9800', top: '38%', left: -52, mt: '-40px' },
                  ].map((c) => (
                    <Box
                      key={c.label}
                      sx={{
                        position: 'absolute',
                        top: c.top, bottom: c.bottom, left: c.left, right: c.right,
                        ml: c.ml, mt: c.mt,
                        width: 80, height: 80,
                        borderRadius: '50%',
                        bgcolor: 'white',
                        boxShadow: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${c.color}`,
                        gap: 0.4,
                      }}
                    >
                      {React.cloneElement(c.icon, { sx: { color: c.color, fontSize: 24 } })}
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        {c.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{ flex: 1 }}
            >
              <Box>
                <Chip
                  label="Smart Circles"
                  sx={{ mb: 2.5, fontWeight: 700, bgcolor: '#e8f5e9', color: '#2e7d32' }}
                />
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{ mb: 2, fontSize: { xs: '1.85rem', md: '2.3rem' }, lineHeight: 1.2 }}
                >
                  The right photos reach the right people - automatically
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, lineHeight: 1.8, fontSize: '1.05rem' }}
                >
                  Your family, friends, colleagues, and VIP guests each get a personalised
                  view of the same event. No manual sorting. No awkward shared links.
                  No privacy worries.
                </Typography>
                <FeatureBullet text="Create circles for any group - family, work, community, VIPs" />
                <FeatureBullet text="Each circle sees only the curated photos you intend for them" />
                <FeatureBullet text="Visual network shows how your contacts are connected across events" />
                <FeatureBullet text="One event, infinite personalised experiences for every guest" />
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* ── FOR ORGANISERS (B2B) ─────────────────────────────────────────────── */}
      <Box
        id="organizers"
        sx={{
          py: { xs: 8, md: 14 },
          background: 'linear-gradient(135deg, #0d1b3e 0%, #1a237e 55%, #0d47a1 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle dot grid overlay */}
        <Box
          sx={{
            position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Chip
                label="For Event Organisers"
                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 700, border: '1px solid rgba(255,255,255,0.2)' }}
              />
            </Box>
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{ mb: 2, fontSize: { xs: '2rem', md: '2.8rem' }, textAlign: { xs: 'center', md: 'left' } }}
            >
              Built for every kind of event
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 8,
                opacity: 0.78,
                fontWeight: 400,
                maxWidth: 600,
                lineHeight: 1.75,
                textAlign: { xs: 'center', md: 'left' },
                mx: { xs: 'auto', md: 0 },
              }}
            >
              Whether you're running a 500-person corporate conference or an intimate family wedding,
              CircleMoments gives you professional-grade photo management - set up in minutes,
              not days.
            </Typography>
          </motion.div>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 8,
            }}
          >
            {organizerFeatures.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.09 }}
              >
                <Box
                  sx={{
                    p: 3.5,
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    height: '100%',
                    transition: 'all 0.25s',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.12)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2, color: '#90caf9', '& svg': { fontSize: 38 } }}>{f.icon}</Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.72, lineHeight: 1.75 }}>
                    {f.description}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          {/* Event type tags */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="body1"
              sx={{ mb: 2.5, opacity: 0.65, textAlign: { xs: 'center', md: 'left' } }}
            >
              Perfect for:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'center', md: 'flex-start' },
                mb: 8,
              }}
            >
              {[
                'Corporate Conferences', 'Weddings', 'Family Reunions', 'Birthday Celebrations',
                'Community Festivals', 'Award Ceremonies', 'College Reunions', 'Sports Events',
              ].map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.09)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.18)',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </motion.div>

          {/* Inline CTA banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.14)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="h5" fontWeight={800} sx={{ mb: 0.75 }}>
                    Ready to transform your next event?
                  </Typography>
                  <Typography sx={{ opacity: 0.72 }}>
                    Set up your event in minutes. No technical knowledge required.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAuth}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: 'white', color: 'primary.dark', py: 1.75, px: 4,
                    fontWeight: 700, fontSize: '1rem', borderRadius: 2,
                    textTransform: 'none', flexShrink: 0,
                    '&:hover': { bgcolor: '#f0f4ff' },
                  }}
                >
                  Create Your Event
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="overline"
              color="primary.main"
              fontWeight={700}
              display="block"
              align="center"
              sx={{ mb: 1, letterSpacing: 3 }}
            >
              GETTING STARTED
            </Typography>
            <Typography
              variant="h3"
              align="center"
              fontWeight={800}
              sx={{ mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              Up and running in minutes
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ mb: 6, maxWidth: 580, mx: 'auto', fontSize: '1.05rem' }}
            >
              Whether you're attending or organising, getting started is effortless.
            </Typography>
          </motion.div>

          <Box sx={{ width: '100%', mb: 6 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              centered
              sx={{
                '& .MuiTabs-flexContainer': { justifyContent: 'center' },
                '& .MuiTab-root': {
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: { xs: 'auto', sm: 190 },
                  px: { xs: 2, sm: 3 },
                },
              }}
            >
              <Tab label="Attending an Event" id="tab-0" aria-controls="tabpanel-0" sx={{ minHeight: { xs: 48, sm: 56 } }} />
              <Tab label="Organising an Event" id="tab-1" aria-controls="tabpanel-1" sx={{ minHeight: { xs: 48, sm: 56 } }} />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            {attendeeSteps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <StepCard {...s} />
              </motion.div>
            ))}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {organizerSteps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <StepCard {...s} />
              </motion.div>
            ))}
          </TabPanel>
        </Container>
      </Box>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 12, md: 16 },
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute', inset: 0, opacity: 0.05,
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '30px 30px',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{ mb: 3, fontSize: { xs: '2rem', md: '2.9rem' }, lineHeight: 1.2 }}
            >
              Your memories deserve better than a WhatsApp group
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 6, opacity: 0.88, fontWeight: 400, maxWidth: 580, mx: 'auto', lineHeight: 1.75 }}
            >
              Join CircleMoments and experience a smarter, more personal way to capture,
              find, and share the moments that matter most.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleAuth}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: 'white', color: 'primary.dark',
                  py: 2, px: 5, fontSize: '1.1rem', fontWeight: 800,
                  borderRadius: 2, textTransform: 'none',
                  '&:hover': { bgcolor: '#f0f4ff' },
                }}
              >
                Get Started Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setContactDialogOpen(true)}
                sx={{
                  borderColor: 'rgba(255,255,255,0.55)',
                  color: 'white',
                  py: 2, px: 4.5, fontSize: '1.1rem', fontWeight: 600,
                  borderRadius: 2, textTransform: 'none',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Contact Us
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <ContactUsDialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} />

      <Footer />
    </Box>
  );
};

export default Landing;
